import {
  Layout,
  SecondaryTopNav,
  Container,
  TextInputFormikField,
  Divider,
  Loader,
} from "@certn/cascadia-react";
import { useClientPortalAPI, useSubmit } from "@certn/api";
import { Text, PrimaryButton } from "@certn/cascadia";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { mutate } from "swr";
import { PackageSelector } from "./components/PackageSelector";
import { Formik } from "formik";
import { useState } from "react";

import { array, object, string } from "yup";
import { CreateEditAdjudicationFlow } from "./components/CreateEditAdjudicationFlow";
import {
  AdjudicationCriteria,
  AdjudicationFormValues,
} from "./types/form-values";
import { CCRCAdjudicationCriteria } from "./components/canadianCriminalRecord/CCRCAdjudicationCriteria";
import { EmploymentVerifAdjudicationCriteria } from "./components/EmploymentVerifAdjudicationCriteria";

const schema = object({
  rubricName: string().required("Rubric name is required"),
  criteria: array().of(object()).required("Criteria is required"),
  packages: array().of(string()),
});

export function CreateAdjudicationPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedCheckType, setSelectedCheckType] = useState<string | null>(
    null
  );
  const [selectedSubCategory, setSelectedSubCategory] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const { data: packagesData, isLoading } = useClientPortalAPI(
    "/api/internal/client-portal/adjudication-rubrics/packages/options/"
  );
  const { data: defaultCriteriaData, isLoading: isDefaultCriteriaLoading } =
    useClientPortalAPI(
      "/api/internal/client-portal/adjudication-rubrics/default-criteria/"
    );

  const { submit, isSubmitting } = useSubmit(
    `/api/internal/client-portal/adjudication-rubrics/create/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: async () => {
        // Invalidate the adjudication rubrics cache to refresh the list
        await mutate("/api/internal/client-portal/adjudication-rubrics/");
        navigate("/adjudication");
      },
    }
  );

  return (
    <>
      {isDefaultCriteriaLoading ? (
        <Loader />
      ) : (
        <Formik<AdjudicationFormValues>
          initialValues={{
            rubricName: "",
            packages: [],
            criteria: (defaultCriteriaData as AdjudicationCriteria[]) || [],
          }}
          onSubmit={(values) => {
            submit({
              name: values.rubricName,
              criteria: values.criteria,
              packages: values.packages,
            });
          }}
          validationSchema={schema}
        >
          {({ handleSubmit, values }) => {
            const steps = {
              EMPLOYMENT_VERIFICATIONS_1: (
                <EmploymentVerifAdjudicationCriteria />
              ),
              BASIC_CANADIAN_CRIMINAL_RECORD_REPORT_1: (
                <CCRCAdjudicationCriteria
                  selectedSubCategory={selectedSubCategory}
                  onSubCategorySelect={setSelectedSubCategory}
                />
              ),
            };

            return (
              <Layout
                layout="narrow"
                header={
                  <SecondaryTopNav
                    testID={
                      selectedCheckType || selectedSubCategory
                        ? "back-button"
                        : "close-button"
                    }
                    leftIcon={
                      selectedCheckType || selectedSubCategory
                        ? "arrow_back"
                        : "close"
                    }
                    onleftIconOnPress={() => {
                      if (selectedSubCategory) {
                        // Handle back from subcategory level to check type level
                        setSelectedSubCategory(null);
                      } else if (selectedCheckType) {
                        // Handle back from check type level to main level
                        setSelectedCheckType(null);
                        setSelectedSubCategory(null);
                      } else {
                        // Handle close from main level
                        navigate("/adjudication");
                      }
                    }}
                  >
                    {!selectedCheckType ? (
                      <PrimaryButton
                        isDisabled={
                          // Disable if submitting or if no rules are set
                          isSubmitting || !values.criteria.some((c) => c.set)
                        }
                        isLoading={isSubmitting}
                        onPress={() => {
                          handleSubmit();
                        }}
                        testID="save-rules-button"
                      >
                        {t("Save rules")}
                      </PrimaryButton>
                    ) : null}
                  </SecondaryTopNav>
                }
              >
                <Container>
                  {selectedCheckType ? (
                    <>{steps[selectedCheckType as keyof typeof steps]}</>
                  ) : (
                    // Show original form content
                    <>
                      <Text style="global/title-medium">
                        {t("Create new rubric")}
                      </Text>
                      <Text style="global/heading-medium">
                        {t("Rubric details")}
                      </Text>
                      <TextInputFormikField
                        name={"rubricName"}
                        testID={"rubricName"}
                        label={t("Rubric name")}
                        isRequired
                      />
                      <Divider />
                      <PackageSelector
                        packagesData={packagesData ?? []}
                        isLoading={isLoading}
                      />
                      <Text style="global/heading-large">
                        {t("Create rules")}
                      </Text>
                      <CreateEditAdjudicationFlow
                        selectedCheckType={selectedCheckType}
                        onNavigationChange={setSelectedCheckType}
                        selectedSubCategory={selectedSubCategory}
                        onSubCategorySelect={setSelectedSubCategory}
                      />
                    </>
                  )}
                </Container>
              </Layout>
            );
          }}
        </Formik>
      )}
    </>
  );
}
